import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { ShieldAlert, Sparkles, Copy, Database, Trash2, Download } from "lucide-react";

const inputCls = "bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const SEV_STYLE = { info: "bg-neutral-50 text-neutral-500 border-neutral-200", low: "bg-sky-50 text-sky-700 border-sky-200",
  medium: "bg-amber-50 text-amber-700 border-amber-200", high: "bg-orange-50 text-orange-700 border-orange-200",
  critical: "bg-red-50 text-red-700 border-red-200" };
const Sev = ({ v }) => <span className={`text-xs font-medium border rounded-full px-2 py-0.5 ${SEV_STYLE[v] || SEV_STYLE.info}`}>{v}</span>;
const TABS = ["Logs", "Dashboards", "Alerts", "Rules", "Compliance"];
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function Siem() {
  const [tab, setTab] = useState("Logs");
  const [config, setConfig] = useState(null);
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({ severity: "", source: "", q: "" });
  const [nl, setNl] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const [nlFilters, setNlFilters] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [rules, setRules] = useState({ builtin: [], custom: [] });
  const [rForm, setRForm] = useState({ name: "", event_type: "", threshold: 5, window_minutes: 10, group_by: "ip", severity: "high" });
  const [comp, setComp] = useState(null);
  const [range, setRange] = useState({ since: "", until: "" });
  const [reports, setReports] = useState([]);
  const [repForm, setRepForm] = useState({ name: "", group_by: "severity", severity: "", source: "", since_hours: "" });

  const loadReports = useCallback(async () => {
    try {
      const { data } = await api.get("/siem/reports");
      const runs = await Promise.all(data.map((r) => api.get(`/siem/reports/${r.id}/run`).then((x) => x.data).catch(() => ({ ...r, data: [] }))));
      setReports(runs);
    } catch { /* noop */ }
  }, []);

  const addReport = async (e) => {
    e.preventDefault();
    try {
      await api.post("/siem/reports", { name: repForm.name, group_by: repForm.group_by,
        filters: { severity: repForm.severity, source: repForm.source, since_hours: repForm.since_hours ? parseFloat(repForm.since_hours) : null } });
      toast.success("Report saved to dashboard");
      setRepForm({ name: "", group_by: "severity", severity: "", source: "", since_hours: "" });
      loadReports();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const delReport = async (r) => {
    if (!window.confirm(`Delete report "${r.name}"?`)) return;
    await api.delete(`/siem/reports/${r.id}`).catch((e) => toast.error(fmtErr(e)));
    loadReports();
  };

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/siem/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.ingested} logs${data.alerts_created.length ? ` — alerts: ${data.alerts_created.join(", ")}` : ""}`);
      loadAll(); loadLogs();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const loadLogs = useCallback(() => {
    const p = new URLSearchParams(Object.entries(filters).filter(([, v]) => v));
    api.get(`/siem/logs?${p}`).then((r) => { setLogs(r.data); setNlFilters(null); }).catch((e) => toast.error(fmtErr(e)));
  }, [filters]);

  const loadAll = useCallback(() => {
    api.get("/siem/config").then((r) => setConfig(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/siem/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    api.get("/siem/rules").then((r) => setRules(r.data)).catch(() => {});
    api.get("/siem/compliance").then((r) => setComp(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadReports(); }, [loadReports]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const runNl = async (e) => {
    e.preventDefault();
    setNlBusy(true);
    try {
      const { data } = await api.post("/siem/nl-query", { query: nl });
      setLogs(data.results);
      setNlFilters(data.filters);
      toast.success(`AI query matched ${data.results.length} logs`);
    } catch (err) { toast.error(fmtErr(err)); }
    setNlBusy(false);
  };

  const demo = async () => {
    try {
      const { data } = await api.post("/siem/demo-data");
      toast.success(`Ingested ${data.ingested} demo logs${data.alerts_created.length ? ` — alerts: ${data.alerts_created.join(", ")}` : ""}`);
      loadAll(); loadLogs();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setAlertStatus = async (a, status) => {
    try { await api.put(`/siem/alerts/${a.id}`, { status }); loadAll(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const alertTicket = async (a) => {
    try { const { data } = await api.post(`/siem/alerts/${a.id}/ticket`); toast.success("P1 ticket created"); loadAll(); return data; }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const addRule = async (e) => {
    e.preventDefault();
    try {
      await api.post("/siem/rules", { ...rForm, threshold: parseInt(rForm.threshold), window_minutes: parseInt(rForm.window_minutes) });
      toast.success("Correlation rule added");
      setRForm({ name: "", event_type: "", threshold: 5, window_minutes: 10, group_by: "ip", severity: "high" });
      api.get("/siem/rules").then((r) => setRules(r.data));
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const delRule = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    await api.delete(`/siem/rules/${r.id}`).catch((e) => toast.error(fmtErr(e)));
    api.get("/siem/rules").then((res) => setRules(res.data));
  };
  const exportCsv = () => {
    const p = new URLSearchParams(Object.entries(range).filter(([, v]) => v));
    window.open(`${API_BASE}/api/siem/compliance/export?${p}`, "_blank");
  };

  return (
    <div data-testid="siem-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h1 className="text-2xl font-bold font-heading flex items-center gap-2"><ShieldAlert size={22} className="text-red-600" /> SIEM — Security Monitoring</h1>
        <div className="flex gap-2">
          <label data-testid="siem-import-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-300 bg-white text-sm font-medium hover:bg-neutral-50 cursor-pointer">
            <Download size={14} className="rotate-180" /> Import CSV / JSON
            <input type="file" accept=".csv,.json" className="hidden" onChange={importFile} data-testid="siem-import-input" />
          </label>
          <button data-testid="siem-demo-button" onClick={demo}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-300 bg-white text-sm font-medium hover:bg-neutral-50">
            <Database size={14} /> Generate demo data
          </button>
        </div>
      </div>

      {config && (
        <div className="bg-[#0B1F33] text-white rounded-2xl p-4 mb-5 flex flex-wrap items-center gap-3 text-xs" data-testid="siem-ingest-panel">
          <span className="font-semibold">Ingest endpoint:</span>
          <code className="bg-white/10 rounded px-2 py-1">POST {API_BASE}/api/siem/ingest</code>
          <span className="font-semibold ml-2">Header X-Ingest-Key:</span>
          <code className="bg-white/10 rounded px-2 py-1" data-testid="siem-ingest-key">{config.ingest_key}</code>
          <button data-testid="siem-copy-key" onClick={() => { navigator.clipboard.writeText(config.ingest_key); toast.success("Key copied"); }}
            className="p-1.5 bg-white/10 hover:bg-white/20 rounded"><Copy size={12} /></button>
          <span className="text-white/50 ml-auto">{config.log_count} logs stored</span>
          <span className="w-full text-white/40" data-testid="siem-connectors-note">
            Connectors: Splunk HEC-compatible → <code className="bg-white/10 rounded px-1">POST {API_BASE}/api/siem/hec</code> (Authorization: Splunk &lt;key&gt;) · generic webhook/JSON → ingest endpoint above · syslog/CloudWatch/Datadog: forward JSON to either endpoint.
          </span>
        </div>
      )}

      <div className="flex gap-1 mb-5">
        {TABS.map((t) => (
          <button key={t} data-testid={`siem-tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-full text-sm font-medium ${tab === t ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-600 hover:bg-neutral-50"}`}>
            {t}{t === "Alerts" && alerts.filter((a) => a.status === "open").length > 0 && <span className="ml-1.5 text-xs bg-red-600 text-white rounded-full px-1.5">{alerts.filter((a) => a.status === "open").length}</span>}
          </button>
        ))}
      </div>

      {tab === "Logs" && (
        <div>
          <form onSubmit={runNl} className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Sparkles size={15} className="absolute left-3 top-3 text-violet-500" />
              <input data-testid="siem-nl-input" value={nl} onChange={(e) => setNl(e.target.value)}
                placeholder='Ask in plain English — e.g. "show failed logins in the last 24 hours"'
                className={inputCls + " w-full pl-9"} />
            </div>
            <button data-testid="siem-nl-run" disabled={nlBusy || nl.trim().length < 3}
              className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
              {nlBusy ? "Translating..." : "Ask AI"}
            </button>
          </form>
          {nlFilters && (
            <p className="text-xs text-neutral-500 mb-3" data-testid="siem-nl-filters">
              AI interpreted: <code className="bg-neutral-100 rounded px-1.5 py-0.5">{JSON.stringify(nlFilters)}</code>
              <button onClick={loadLogs} className="ml-2 underline" data-testid="siem-nl-clear">clear</button>
            </p>
          )}
          <div className="flex flex-wrap gap-2 mb-3">
            <select data-testid="siem-severity-filter" value={filters.severity} onChange={(e) => setFilters((f) => ({ ...f, severity: e.target.value }))} className={inputCls}>
              <option value="">All severities</option>
              {(config?.severities || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select data-testid="siem-source-filter" value={filters.source} onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))} className={inputCls}>
              <option value="">All sources</option>
              {(config?.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <input data-testid="siem-search" placeholder="Search message..." value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} className={inputCls + " w-60"} />
          </div>
          <div className="bg-white border border-neutral-200 rounded-2xl overflow-hidden">
            {logs.length === 0 ? <p className="p-6 text-sm text-neutral-400" data-testid="siem-logs-empty">No logs — click "Generate demo data" or point your systems at the ingest endpoint.</p> : (
              <div className="overflow-x-auto max-h-[520px] overflow-y-auto"><table className="min-w-full divide-y divide-neutral-200 text-xs text-left">
                <thead className="sticky top-0 bg-neutral-50"><tr>
                  {["Time", "Source", "Severity", "Event", "User", "IP", "Message"].map((h) => <th key={h} className="px-4 py-2.5 font-medium text-neutral-500 uppercase tracking-wider">{h}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-neutral-100">
                  {logs.map((l) => (
                    <tr key={l.id} data-testid={`siem-log-${l.id}`} className="hover:bg-neutral-50">
                      <td className="px-4 py-2 whitespace-nowrap text-neutral-400">{new Date(l.ts).toLocaleString()}</td>
                      <td className="px-4 py-2">{l.source}</td>
                      <td className="px-4 py-2"><Sev v={l.severity} /></td>
                      <td className="px-4 py-2 font-mono">{l.event_type}</td>
                      <td className="px-4 py-2">{l.user || "—"}</td>
                      <td className="px-4 py-2 font-mono">{l.ip || "—"}</td>
                      <td className="px-4 py-2 text-neutral-600 max-w-[300px] truncate">{l.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table></div>
            )}
          </div>
        </div>
      )}

      {tab === "Dashboards" && (
        <div data-testid="siem-dashboards">
          <form onSubmit={addReport} className="bg-white border border-neutral-200 rounded-2xl p-5 mb-5 flex flex-wrap items-end gap-3" data-testid="siem-report-form">
            <label className="text-xs text-neutral-500">Report name
              <input data-testid="report-name-input" required minLength={3} value={repForm.name}
                onChange={(e) => setRepForm((f) => ({ ...f, name: e.target.value }))} className={inputCls + " block mt-1 w-52"} /></label>
            <label className="text-xs text-neutral-500">Group by
              <select data-testid="report-groupby-select" value={repForm.group_by} onChange={(e) => setRepForm((f) => ({ ...f, group_by: e.target.value }))} className={inputCls + " block mt-1"}>
                {["severity", "source", "event_type", "user", "ip", "hour"].map((g) => <option key={g} value={g}>{g}</option>)}
              </select></label>
            <label className="text-xs text-neutral-500">Severity filter
              <select data-testid="report-severity-select" value={repForm.severity} onChange={(e) => setRepForm((f) => ({ ...f, severity: e.target.value }))} className={inputCls + " block mt-1"}>
                <option value="">any</option>
                {(config?.severities || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select></label>
            <label className="text-xs text-neutral-500">Last N hours
              <input data-testid="report-hours-input" type="number" min="1" value={repForm.since_hours} placeholder="all time"
                onChange={(e) => setRepForm((f) => ({ ...f, since_hours: e.target.value }))} className={inputCls + " block mt-1 w-28"} /></label>
            <button data-testid="report-save-button" className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Add report</button>
          </form>
          {reports.length === 0 && <p className="text-sm text-neutral-400" data-testid="siem-reports-empty">No reports yet — build one above and it becomes a live dashboard widget.</p>}
          <div className="grid md:grid-cols-2 gap-4">
            {reports.map((r) => {
              const max = Math.max(...(r.data || []).map((d) => d.count), 1);
              return (
                <div key={r.id} data-testid={`siem-report-${r.id}`} className="bg-white border border-neutral-200 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-semibold text-sm">{r.name} <span className="text-xs text-neutral-400 font-normal">by {r.group_by}</span></p>
                    <button data-testid={`report-delete-${r.id}`} onClick={() => delReport(r)} className="p-1 text-red-400 hover:text-red-700"><Trash2 size={13} /></button>
                  </div>
                  {(r.data || []).length === 0 ? <p className="text-xs text-neutral-400">No matching logs.</p> : r.data.map((d) => (
                    <div key={d.label} className="flex items-center gap-2 mb-1.5">
                      <span className="w-28 text-xs text-neutral-500 truncate text-right">{d.label}</span>
                      <div className="flex-1 h-3 bg-neutral-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#0B1F33] rounded-full" style={{ width: `${(d.count / max) * 100}%` }} />
                      </div>
                      <span className="w-8 text-xs text-neutral-500">{d.count}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "Alerts" && (
        <div className="space-y-3" data-testid="siem-alerts-list">
          {alerts.length === 0 && <p className="text-sm text-neutral-400">No alerts — correlation rules run automatically on every ingest.</p>}
          {alerts.map((a) => (
            <div key={a.id} data-testid={`siem-alert-${a.id}`} className="bg-white border border-neutral-200 rounded-2xl p-5 flex flex-wrap items-center gap-3">
              <Sev v={a.severity} />
              <div className="flex-1 min-w-[220px]">
                <p className="font-semibold text-sm">{a.rule_name} — <span className="font-mono">{a.entity}</span></p>
                <p className="text-xs text-neutral-500 mt-0.5">{a.detail}</p>
                <p className="text-xs text-neutral-400 mt-0.5">{new Date(a.created_at).toLocaleString()} · status: <b>{a.status}</b></p>
              </div>
              {a.ticket_id ? (
                <Link to={`/desk/tickets/${a.ticket_id}`} data-testid={`alert-ticket-link-${a.id}`} className="text-xs text-sky-700 underline">View ticket</Link>
              ) : (
                <button data-testid={`alert-create-ticket-${a.id}`} onClick={() => alertTicket(a)}
                  className="px-3 py-1.5 border border-neutral-300 rounded-md text-xs font-medium hover:bg-neutral-50">Create ticket</button>
              )}
              {a.status !== "acknowledged" && a.status !== "resolved" && (
                <button data-testid={`alert-ack-${a.id}`} onClick={() => setAlertStatus(a, "acknowledged")}
                  className="px-3 py-1.5 border border-amber-300 text-amber-700 rounded-md text-xs font-medium hover:bg-amber-50">Acknowledge</button>
              )}
              {a.status !== "resolved" && (
                <button data-testid={`alert-resolve-${a.id}`} onClick={() => setAlertStatus(a, "resolved")}
                  className="px-3 py-1.5 border border-emerald-300 text-emerald-700 rounded-md text-xs font-medium hover:bg-emerald-50">Resolve</button>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === "Rules" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <div>
            <h3 className="font-semibold font-heading text-sm mb-3">Built-in correlation rules</h3>
            <div className="space-y-2">
              {rules.builtin.map((r) => (
                <div key={r.id} className="bg-white border border-neutral-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-medium flex items-center gap-2">{r.name} <Sev v={r.severity} /></p>
                  <p className="text-xs text-neutral-500 mt-0.5">{r.desc}</p>
                </div>
              ))}
            </div>
            <h3 className="font-semibold font-heading text-sm mt-5 mb-3">Custom rules</h3>
            <div className="space-y-2" data-testid="siem-custom-rules">
              {rules.custom.length === 0 && <p className="text-xs text-neutral-400">No custom rules yet.</p>}
              {rules.custom.map((r) => (
                <div key={r.id} data-testid={`siem-rule-${r.id}`} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3">
                  <div className="flex-1">
                    <p className="text-sm font-medium flex items-center gap-2">{r.name} <Sev v={r.severity} /></p>
                    <p className="text-xs text-neutral-500 mt-0.5">{r.desc}</p>
                  </div>
                  <button data-testid={`siem-rule-delete-${r.id}`} onClick={() => delRule(r)} className="p-1.5 text-red-400 hover:text-red-700 hover:bg-red-50 rounded-md"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </div>
          <form onSubmit={addRule} className="bg-white border border-neutral-200 rounded-2xl p-6 h-fit" data-testid="siem-rule-form">
            <h3 className="font-semibold font-heading text-sm mb-4">New correlation rule</h3>
            <input data-testid="rule-name-input" required minLength={3} placeholder="Rule name" value={rForm.name}
              onChange={(e) => setRForm((f) => ({ ...f, name: e.target.value }))} className={inputCls + " w-full"} />
            <input data-testid="rule-event-input" required placeholder="event_type (e.g. auth_failure)" value={rForm.event_type}
              onChange={(e) => setRForm((f) => ({ ...f, event_type: e.target.value }))} className={inputCls + " w-full mt-2 font-mono"} />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <label className="text-xs text-neutral-500">Threshold (events)
                <input data-testid="rule-threshold-input" type="number" min="2" required value={rForm.threshold}
                  onChange={(e) => setRForm((f) => ({ ...f, threshold: e.target.value }))} className={inputCls + " w-full mt-1"} /></label>
              <label className="text-xs text-neutral-500">Window (minutes)
                <input data-testid="rule-window-input" type="number" min="1" required value={rForm.window_minutes}
                  onChange={(e) => setRForm((f) => ({ ...f, window_minutes: e.target.value }))} className={inputCls + " w-full mt-1"} /></label>
              <label className="text-xs text-neutral-500">Group by
                <select data-testid="rule-groupby-select" value={rForm.group_by} onChange={(e) => setRForm((f) => ({ ...f, group_by: e.target.value }))} className={inputCls + " w-full mt-1"}>
                  <option value="ip">IP address</option><option value="user">User</option>
                </select></label>
              <label className="text-xs text-neutral-500">Alert severity
                <select data-testid="rule-severity-select" value={rForm.severity} onChange={(e) => setRForm((f) => ({ ...f, severity: e.target.value }))} className={inputCls + " w-full mt-1"}>
                  {["low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
                </select></label>
            </div>
            <p className="text-xs text-neutral-400 mt-2">Critical alerts automatically open a P1 ticket.</p>
            <button data-testid="rule-save-button" className="mt-3 w-full py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Add rule</button>
          </form>
        </div>
      )}

      {tab === "Compliance" && comp && (
        <div data-testid="siem-compliance">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[["Total logs retained", comp.total_logs], ["Total alerts", comp.total_alerts],
              ["Oldest log", comp.oldest_log ? new Date(comp.oldest_log).toLocaleDateString() : "—"],
              ["Critical events", comp.by_severity.critical]].map(([k, v]) => (
              <div key={k} className="bg-white border border-neutral-200 rounded-2xl p-4">
                <p className="text-xs text-neutral-500 uppercase tracking-wider">{k}</p>
                <p className="text-2xl font-bold font-heading mt-1">{v}</p>
              </div>
            ))}
          </div>
          <div className="bg-white border border-neutral-200 rounded-2xl p-6">
            <p className="text-sm text-neutral-600">{comp.retention_policy}</p>
            <div className="flex flex-wrap items-end gap-3 mt-4">
              <label className="text-xs text-neutral-500">From
                <input data-testid="comp-since" type="date" value={range.since} onChange={(e) => setRange((r) => ({ ...r, since: e.target.value }))} className={inputCls + " block mt-1"} /></label>
              <label className="text-xs text-neutral-500">To
                <input data-testid="comp-until" type="date" value={range.until} onChange={(e) => setRange((r) => ({ ...r, until: e.target.value }))} className={inputCls + " block mt-1"} /></label>
              <button data-testid="comp-export-button" onClick={exportCsv}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">
                <Download size={14} /> Export audit report (CSV)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
